// ---------------------------------------------------------------------------
// 📣 Updates — the project's broadcast channel. Anyone on the team can post;
// every member gets it in their inbox (Odoo notification under the hood).
// ---------------------------------------------------------------------------

import { useEffect, useRef, useState } from 'react';
import { Megaphone, X } from 'lucide-react';
import type { Area, TaskMessage } from '@cg-projects/shared';
import { Icon } from '@/core/ui/icons';
import { api } from '@/core/api/apiClient';
import { newPostKey } from '@/core/api/postKey';
import { useToastStore } from '@/core/ui/toastStore';
import { initials, avatarColor } from '@/core/ui/helpers';
import { Spinner } from '@/core/ui/atoms';

function esc(t: string): string {
  return t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');
}
function fmt(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function UpdatesPanel({ area, onClose }: { area: Area; onClose: () => void }) {
  const toast = useToastStore((s) => s.show);
  const [messages, setMessages] = useState<TaskMessage[] | null>(null);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  // Draft-scoped idempotency key — see newPostKey(). Held across retries of this
  // update, rotated only once it lands.
  const postKeyRef = useRef<string>(newPostKey());

  useEffect(() => {
    let alive = true;
    api.get<{ messages: TaskMessage[] }>(`/areas/${area.id}/updates`)
      .then((r) => { if (alive) setMessages(r.messages); })
      .catch(() => { if (alive) setMessages([]); });
    return () => { alive = false; };
  }, [area.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const t = e.target as HTMLElement | null;
      if (t && /^(INPUT|TEXTAREA)$/.test(t.tagName)) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const post = async () => {
    const text = draft.trim();
    if (!text || posting) return;
    setPosting(true);
    try {
      const { message } = await api.post<{ message: TaskMessage }>(`/areas/${area.id}/updates`, { body: esc(text), clientKey: postKeyRef.current });
      // Landed — rotate so the NEXT update is a new logical post. Retries of THIS
      // one keep the same key, so a re-send after a lost response is collapsed
      // instead of announcing to the whole team twice.
      postKeyRef.current = newPostKey();
      setMessages((m) => [...(m ?? []), message]);
      setDraft('');
      toast('Posted — the whole team gets it in their inbox.', 'info');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not post the update.', 'error');
    } finally {
      setPosting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/60 animate-fade-in" onClick={onClose} />
      <div className="fixed inset-x-3 inset-y-6 z-50 mx-auto flex max-w-2xl flex-col overflow-hidden rounded-xl border border-line bg-canvas shadow-2xl animate-fade-in md:inset-y-12">
        <div className="flex h-12 shrink-0 items-center justify-between border-b border-line px-4">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink"><Icon i={Megaphone} size={14} /> Updates</span>
            <span className="ml-2 truncate text-xs text-faint">{area.name} — everyone on the team gets these in their inbox</span>
          </div>
          <button onClick={onClose} className="rounded p-1 text-muted hover:bg-hover hover:text-ink" title="Close (Esc)"><Icon i={X} size={14} /></button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {messages === null && <Spinner label="Loading updates…" />}
          {messages !== null && messages.length === 0 && (
            <div className="py-10 text-center text-sm text-faint">
              No updates yet. Post the first one — kickoffs, milestones, schedule changes, wins.
            </div>
          )}
          <div className="space-y-4">
            {(messages ?? []).map((m) => (
              <div key={m.id} className="flex gap-2.5">
                <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-medium text-white" style={{ backgroundColor: avatarColor(m.authorName) }}>{initials(m.authorName)}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[13px] font-medium text-ink">{m.authorName}</span>
                    <span className="text-[10px] text-faint">{fmt(m.date)}</span>
                  </div>
                  <div className="cg-prose text-[13px]" dangerouslySetInnerHTML={{ __html: m.body }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="shrink-0 border-t border-line p-3">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void post(); } }}
            rows={2}
            placeholder="Share an update with the whole team… (⌘/Ctrl+Enter to post)"
            className="w-full resize-none rounded-md border border-line bg-page px-2.5 py-1.5 text-[13px] text-ink outline-none placeholder:text-faint focus:border-brand"
          />
          <div className="mt-1.5 flex justify-end">
            <button onClick={() => void post()} disabled={posting || !draft.trim()}
              className="flex items-center gap-1 rounded-md bg-brand px-3 py-1 text-xs font-semibold text-page transition hover:brightness-110 disabled:opacity-50">
              {posting ? 'Posting…' : <><Icon i={Megaphone} size={14} /> Post update</>}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
